import { store } from '@/store';
import { isTauri } from '@/tauri/utils';
import { readProviderCookie } from '@/utils/auth/authProviderCookie';
import {
  type AuthProvider,
  getPersistedAuthProvider,
} from '@/utils/auth/persistedAuthProvider';
import { readAuthRevision } from '@heidi/auth/state';

/**
 * Resolves the effective auth provider for cache-routing decisions.
 *
 * On web, the `heidi-auth-provider` cookie is authoritative — it's set
 * server-side by the OAuth callback before Redux can update, and the
 * Kinde→Auth0 migration flow relies on the cookie flipping ahead of
 * Redux. Falls back to Redux when the cookie is unset or holds an
 * unrecognised value (covers first-boot-with-persisted-state and edge
 * cases where the cookie was cleared but Redux still carries the prior
 * provider).
 *
 * On desktop, the OAuth handshake doesn't set browser cookies, so
 * Redux remains authoritative.
 *
 * Used by every site that branches on the active provider — dispatcher,
 * scheduler, useAuth0Session shim, useIsAuthenticated. Centralising the
 * logic prevents the four sites from drifting apart on the cookie-vs-
 * Redux question.
 *
 * ## Reactivity contract — IMPORTANT for callers
 *
 * The cookie read is sync and non-subscribed. No browser API fires when
 * `document.cookie` is mutated (the Cookie Store API is Chromium-only
 * and not yet portable), so React hooks consuming this helper only
 * re-render when something else changes — typically a Jotai atom written
 * by the cache, or a `useSelector`-subscribed Redux value.
 *
 * The implication: **any code path that writes or clears the cookie
 * MUST pair the write with an atom-driving call** so consumers actually
 * notice. The current callers all satisfy this:
 *
 * - OAuth callback (server-side cookie write) → full page navigation →
 *   scheduler re-mounts → `getAccessToken()` fires → atoms transition.
 * - Kinde→Auth0 migration polling (`useKindeMigrationCompletion`) →
 *   `refreshSession()` → `getAccessToken({ forceRefresh: true })` →
 *   atoms transition.
 * - Forced-logout cookie clear (in `useSessionInvalidationTelemetry`)
 *   → paired with `debouncedExpiryLogout` → drives Redux + atom clears.
 *
 * If a new flow flips the cookie without triggering a fetch (or
 * `clearAuthCache`), consumers will keep returning the stale value
 * until some unrelated atom transition or Redux change forces a
 * re-render. Worst case: provider-breakdown reports the wrong provider
 * until the next focus event runs the scheduler.
 */
export function getEffectiveAuthProvider(): AuthProvider | null {
  const reduxProvider = store.getState().auth.authProvider ?? null;
  if (isTauri()) {
    // Fall back to the persisted-provider read when Redux is null.
    // This covers ONLY the pre-rehydration window — the moment
    // between module import and redux-persist finishing its
    // rehydration into Redux. `getPersistedAuthProvider()` is not
    // a separate store: it reads `authProvider` out of the SAME
    // redux-persist auth slice via `readPersistedAuthSlice`, so a
    // legacy install whose persisted slice never had `authProvider`
    // written gets `undefined` from BOTH reads. Residual legacy
    // state is healed by the always-dispatch paths at the source:
    // Auth0-desktop dispatches `setAuthProviderAsSessionCookie(
    // AUTH0_DESKTOP)` on both boot-restore paths in
    // `src/tauri/auth0/provider.tsx`, and Kinde-desktop dispatches
    // `setAuthProviderAsSessionCookie(KINDE)` inside the
    // post-init effect in `src/components/utils/tauriAuth.tsx`
    // whenever `authProvider !== KINDE`. This fallback is kept
    // because PersistGate doesn't gate every cache-driven caller
    // and a pre-rehydration read of `state.auth.authProvider`
    // would otherwise fall into the dispatcher's null-provider
    // transient gate for the microtask window before rehydration
    // completes.
    return reduxProvider ?? getPersistedAuthProvider() ?? null;
  }
  return readProviderCookie() ?? reduxProvider;
}

/**
 * The runtime-only auth revision, paired here with the effective provider
 * `getEffectiveAuthProvider` resolves so both halves of an auth identity are
 * read through one module.
 *
 * The counter itself no longer lives in the `auth` Redux slice — it is a
 * package-owned register (`@heidi/auth/state`) advanced by the
 * `(token, authProvider)` observer wired in `src/store.ts`. Synchronous, and
 * must stay that way: callers pair it with a token inside one continuation.
 */
export function getCurrentAuthRevision(): number {
  return readAuthRevision();
}
